import React, { useMemo, useState, useEffect } from "react";
import {
  Box, Typography, Avatar, Chip, CircularProgress, Button, IconButton, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, useTheme, Tooltip, Collapse,
  Divider
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from "@mui/icons-material";
import FilterBar from "../../../../components/FilterNSearchBar/FilterNSearchBar";
import { useQuery, useMutation } from "@apollo/client";
import { EmptyFields, CheckStatus, CheckStatusLabel } from "../../../../utils/enums";
import { GET_STAFF_DETAILS, GET_PEOPLE_CHECK_EXCEPTIONS, CREATE_RESOURCE_PEOPLE_CHECK_EXCEPTION, DELETE_PEOPLE_CHECK_EXCEPTION } from "../../../../apis/organization-apis/people-apis/peopleApis";
import { DeleteExceptionDialog } from "../../../Settings/Frameworks/FrameWorkDialogs/CheckDetails/ExceptionComponents/DeleteExceptionDialog";
import { ExceptionDetailsForm } from "../../../Settings/Frameworks/FrameWorkDialogs/CheckDetails/ExceptionComponents/ExceptionDetailsForm";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import { blue, grey } from "@mui/material/colors";
import { useToday, formatDate } from "../../../../utils/dateUtils";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import { CheckStatusChip } from "../../../../components/CheckStatusChip/CheckStatusChip";
import ResourceSelectCell from "../common/ResourceSelectCell";
import ResourceStatusCell from "../common/ResourceStatusCell";
import ResourceReasonCell from "../common/ResourceReasonCell";
import ResourcesTableContainer from "../common/ResourcesTableContainer";
import BulkDeleteExceptionDialog from "../common/BulkDeleteExceptionDialog";
import ExceptionActionMenu from "../common/ExceptionActionMenu";

// Shared styles
const buttonBaseStyles = {
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "14px",
  px: 2,
  py: 1,
};


const PeopleResourcesTab = ({ checkDetails, activeCheckId, onRefetch, onStartPolling, globalException, isPollingRunStatus = false }) => {
  const theme = useTheme();
  const today = useToday();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [createExceptionOpen, setCreateExceptionOpen] = useState(false);
  const [exceptionFormData, setExceptionFormData] = useState({ reason: "", validUntil: "" });
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exceptionToDelete, setExceptionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [tableExpanded, setTableExpanded] = useState(true);
  const [tableFilters, setTableFilters] = useState({});

  const isValidUntilPast = exceptionFormData.validUntil && exceptionFormData.validUntil < today;

  const { data: staffData } = useQuery(GET_STAFF_DETAILS);
  const { data: exceptionsData, refetch: refetchLocalExceptions } = useQuery(GET_PEOPLE_CHECK_EXCEPTIONS, {
    variables: { filter: { active_check_id: Number(activeCheckId) }, page: 1, limit: 1000 },
    skip: !activeCheckId,
  });

  const [createResourceException, { loading: creatingException }] = useMutation(CREATE_RESOURCE_PEOPLE_CHECK_EXCEPTION);
  const [deleteException] = useMutation(DELETE_PEOPLE_CHECK_EXCEPTION);

  useEffect(() => {
    if (refetchLocalExceptions) {
      refetchLocalExceptions();
    }
  }, [globalException?.id, refetchLocalExceptions]);

  const employees = useMemo(() => {
    if (!checkDetails?.employee_results) return [];
    const staffList = staffData?.getEmployees?.data || [];
    const exceptions = exceptionsData?.getPeopleCheckExceptions?.data || [];
    const hasGlobalException = !!globalException;
    
    return checkDetails.employee_results.map((res) => {
      const email = res?.resource_name;
      const staffDetails = staffList.find((emp) => 
        emp?.work_email?.toLowerCase() === email?.toLowerCase()
      );
      const employeeException = exceptions.find((exc) => exc?.employee_email === email && exc?.is_active === true);
      const hasException = !!employeeException;
      
      return {
        id: res?.id || res?.resource_id || email,
        name: staffDetails?.name || email?.split("@")[0].slice(0,1).toUpperCase() + email?.split("@")[0].slice(1) || EmptyFields.UNKNOWN,
        email,
        role: staffDetails?.employments?.[0]?.job_title || "-",
        status: hasGlobalException || hasException ? "disabled" : (res?.status || "Unknown"),
        reason: hasGlobalException 
          ? "-" 
          : (hasException && employeeException?.reason ? employeeException.reason : (res?.reason || "-")),
        exceptionId: employeeException?.id || null,
        exceptionData: employeeException || null,
        exceptionValidUntil: hasException && employeeException?.exception_valid_till ? employeeException.exception_valid_till : null,
      };
    });
  }, [checkDetails, staffData, exceptionsData, globalException]);

  const normalizeStatus = (status = "") => {
    const normalized = status?.toLowerCase() || "";
    if (normalized.includes(CheckStatus.PASS)) return CheckStatus.PASS;
    if (normalized.includes(CheckStatus.FAIL)) return CheckStatus.FAIL;
    if (normalized.includes(CheckStatus.DISABLED)) return CheckStatus.DISABLED;
    return CheckStatus.PENDING;
  };

  const filteredEmployees = useMemo(() => {
    let filtered = employees;
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter((emp) => 
        emp?.name?.toLowerCase().includes(query) || emp?.email?.toLowerCase().includes(query) || emp?.role?.toLowerCase().includes(query)
      );
    }
    const statusFilters = tableFilters?.status || [];
    if (statusFilters.length > 0) {
      filtered = filtered.filter((emp) => statusFilters.includes(normalizeStatus(emp?.status)));
    }

    Object.entries(tableFilters || {}).forEach(([field, values]) => {
      if (field === "status" || !values || values.length === 0) return;
      filtered = filtered.filter((emp) => {
        const value = emp?.[field];
        const normalizedValue = value === null || value === undefined ? "" : String(value);
        return values.includes(normalizedValue);
      });
    });

    return filtered;
  }, [employees, searchTerm, tableFilters]);

  const clearFilters = () => {
    setSearchTerm("");
    setTableFilters({});
  };

  const handleTableFilterChange = (filters) => {
    setTableFilters(filters || {});
  };

  const statusFilterOptions = useMemo(() => ([
    { id: CheckStatus.PASS, label: CheckStatusLabel.PASSING },
    { id: CheckStatus.FAIL, label: CheckStatusLabel.FAILING },
    { id: CheckStatus.PENDING, label: CheckStatusLabel.PENDING },
    { id: CheckStatus.DISABLED, label: CheckStatusLabel.DISABLED },
  ]), []);

  const roleFilterOptions = useMemo(() => {
    const roles = new Set();
    employees.forEach((emp) => {
      const value = emp?.role && emp.role !== "-" ? emp.role : "-";
      if (value) {
        roles.add(value);
      }
    });
    return Array.from(roles).map((role) => ({
      id: role,
      label: role === "-" ? "Not Assigned" : role,
    }));
  }, [employees]);

  const handleSelectEmployee = (email) => {
    setSelectedEmployees((prev) => prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]);
  };

  const handleSelectAll = () => {
    setSelectedEmployees(selectedEmployees.length === filteredEmployees.length ? [] : filteredEmployees.map((emp) => emp.email));
  };

  const handleOpenCreateException = (employeeEmail = null) => {
    if (employeeEmail) {
      setSelectedEmployees([employeeEmail]);
    }
    setCreateExceptionOpen(true);
  };

  const handleCloseCreateException = () => {
    setCreateExceptionOpen(false);
    setExceptionFormData({ reason: "", validUntil: "" });
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const result = await deleteException({
        variables: { ids: [Number(exceptionToDelete?.id)] },
      });
      
      if (result?.data?.deletePeopleCheckException?.success) {
        setDeleteDialogOpen(false);
        setExceptionToDelete(null);
        setSnackbarInfo({
          message: result.data.deletePeopleCheckException.message || "Exception deleted successfully!",
          vertical: "top",
          horizontal: "right",
        });
        if (onStartPolling) onStartPolling();
        refetchLocalExceptions();
        onRefetch();
      } else {
        setSnackbarInfo({
          message: result?.data?.deletePeopleCheckException?.message || "Failed to delete exception.",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to delete exception.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const selectedExceptionRecords = useMemo(() => {
    if (!selectedEmployees?.length) return [];
    const byEmail = new Map(
      employees.map((emp) => [emp.email, emp.exceptionData])
    );
    return selectedEmployees
      .map((email) => byEmail.get(email))
      .filter((rec) => rec && rec.id != null);
  }, [selectedEmployees, employees]);

  const handleConfirmBulkDelete = async () => {
    const ids = selectedExceptionRecords.map((rec) => Number(rec.id));
    if (!ids.length) return;
    setDeleteLoading(true);
    try {
      const result = await deleteException({ variables: { ids } });
      if (result?.data?.deletePeopleCheckException?.success) {
        setBulkDeleteOpen(false);
        setSelectedEmployees([]);
        setSnackbarInfo({
          message:
            result.data.deletePeopleCheckException.message ||
            `${ids.length} exceptions deleted successfully!`,
          vertical: "top",
          horizontal: "right",
        });
        if (onStartPolling) onStartPolling();
        refetchLocalExceptions();
        onRefetch();
      } else {
        setSnackbarInfo({
          message:
            result?.data?.deletePeopleCheckException?.message ||
            "Failed to delete exceptions.",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to delete exceptions.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreateException = async () => {
    if (!exceptionFormData.reason.trim()) {
      setSnackbarInfo({ message: "Please provide justification", vertical: "top", horizontal: "right" });
      return;
    }
    if (!exceptionFormData.validUntil) {
      setSnackbarInfo({ message: "Please provide expiration date", vertical: "top", horizontal: "right" });
      return;
    }
    if (exceptionFormData.validUntil < today) {
      setSnackbarInfo({ message: "Expiration date cannot be in the past", vertical: "top", horizontal: "right" });
      return;
    }

    try {
      const result = await createResourceException({
        variables: {
          exception_data: {
            active_check_id: Number(activeCheckId),
            reason: exceptionFormData.reason,
            employee_emails: selectedEmployees,
            exception_valid_till: exceptionFormData.validUntil,
          },
        },
      });

      if (result.data?.createResourceLevelPeopleCheckException?.success) {
        setSnackbarInfo({ message: `Exception created for ${selectedEmployees.length} employee${selectedEmployees.length > 1 ? 's' : ''}!`, vertical: "top", horizontal: "right" });
        setSelectedEmployees([]);
        handleCloseCreateException();
        if (onStartPolling) onStartPolling();
        await refetchLocalExceptions();
        if (onRefetch) await onRefetch();
      } else {
        setSnackbarInfo({ message: result.data?.createResourceLevelPeopleCheckException?.message || "Failed to create exception", vertical: "top", horizontal: "right" });
      }
    } catch (error) {
      console.error("Exception creation error:", error);
      setSnackbarInfo({ message: error?.message || "Failed to create exception", vertical: "top", horizontal: "right" });
    } finally {
    }
  };


  const employeeColumns = [
    { 
      label: (
        <Checkbox
          checked={selectedEmployees.length === filteredEmployees.length && filteredEmployees.length > 0}
          indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < filteredEmployees.length}
          onChange={handleSelectAll}
          disabled={!!globalException}
          sx={{ color: "#031B89", "&.Mui-checked": { color: "#031B89" }, "&.MuiCheckbox-indeterminate": { color: "#031B89" } }}
        />
      ), 
      field: "select", 
      width: "7%",
      minWidth: "60px",
      align: "center"
    },
    { label: "Employee Name", field: "name", align: "left", width: "17%" },
    { 
      label: "Role", 
      field: "role", 
      align: "center", 
      width: "17%",
      filterConfig: roleFilterOptions.length ? { filterOptions: roleFilterOptions } : undefined,
    },
    { label: "Email ID", field: "email", align: "left", width: "17%" },
    { 
      label: "Status", 
      field: "status", 
      align: "center", 
      width: "18%",
      filterConfig: {
        filterOptions: statusFilterOptions,
      },
    },
    { label: "Reason", field: "reason", align: "left", width: "26%" },
  ];

  const renderEmployeeCell = (row, column) => {
    if (column?.field === "select") {
      return (
        <ResourceSelectCell
          resourceId={row.email}
          selectedResources={selectedEmployees}
          onSelect={handleSelectEmployee}
          disabled={!!globalException}
        />
      );
    }
    if (column?.field === "name") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Avatar sx={{ bgcolor: blue[600], mr: 2, width: 40, height: 40, fontSize: "0.9375rem", fontWeight: 600, flexShrink: 0 }}>
            {row?.name?.split(" ")?.map((n) => n?.[0])?.join("")}
          </Avatar>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "0.9375rem", overflow: "hidden", textOverflow: "ellipsis" }}>
            {row?.name || EmptyFields.UNKNOWN}
          </Typography>
        </Box>
      );
    }
    if (column?.field === "role") {
      const hasRole = row?.role && row?.role !== "-";
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Chip 
            label={row?.role === "-" ? "Not Assigned" : row?.role} 
            size="small"
            sx={{ 
              bgcolor: hasRole ? "rgba(26,86,219,0.08)" : "background.paper", 
              color: hasRole ? "primary.main" : "text.secondary",
              fontWeight: 500, 
              fontSize: "0.8125rem", 
              height: "26px", 
              borderRadius: "6px", 
              px: 1.5,
              maxWidth: "100%",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis"
              }
            }}
          />
        </Box>
      );
    }
    if (column?.field === "email") {
      return (
        <Typography variant="body2" sx={{ color: grey[600], fontSize: "0.875rem", overflow: "hidden", textOverflow: "ellipsis" }}>
          {row?.email || "-"}
        </Typography>
      );
    }
    if (column?.field === "status") {
      return (
        <ResourceStatusCell 
          status={row.status} 
          exceptionValidUntil={row.exceptionValidUntil}
          gap={1}
          isRefreshing={isPollingRunStatus}
        />
      );
    }
    if (column?.field === "reason") {
      const hasException = row?.exceptionId && row?.exceptionData;
      const isEmployeeSelected = selectedEmployees.includes(row.email);
      const isBulkMode = selectedEmployees.length >= 2;
      const isDeleteDisabled =
        !hasException ||
        (isBulkMode && isEmployeeSelected) ||
        !!globalException ||
        isPollingRunStatus;

      return (
        <ResourceReasonCell
          reason={row.reason}
          hasException={hasException}
          onDelete={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (!isDeleteDisabled) {
              setExceptionToDelete(row.exceptionData);
              setDeleteDialogOpen(true);
            }
          }}
          disabled={isDeleteDisabled}
          hasGlobalException={!!globalException}
          isBulkMode={isBulkMode}
          isResourceSelected={isEmployeeSelected}
        />
      );
    }
    return <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.875rem" }}>{row[column?.field] || "-"}</Typography>;
  };

  const hasAnyEmployees = employees && employees.length > 0;
  if (!hasAnyEmployees) {
    return null;
  }
  
  return (
    <>
      <ResourcesTableContainer
        title="Resources"
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        clearFilters={clearFilters}
        filteredCount={filteredEmployees.length}
        countLabel={`${filteredEmployees.length} ${filteredEmployees.length === 1 ? 'employee' : 'employees'} found`}
        tableExpanded={tableExpanded}
        onToggleExpand={() => setTableExpanded(!tableExpanded)}
        selectedCount={selectedEmployees.length}
        selectedLabel="Selected"
        markExceptionButton={
          !globalException && (
            <ExceptionActionMenu
              disabled={
                selectedEmployees.length === 0 ||
                creatingException ||
                isPollingRunStatus
              }
              disabledTooltip={
                isPollingRunStatus
                  ? "Please wait for the check to finish updating"
                  : selectedEmployees.length === 0
                  ? "Select at least one employee to manage exceptions"
                  : ""
              }
              onMark={() => handleOpenCreateException()}
              markDisabled={creatingException}
              markDescription="Create exception for selected employees"
              onDelete={() => setBulkDeleteOpen(true)}
              deleteDisabled={
                selectedExceptionRecords.length === 0 ||
                deleteLoading ||
                isPollingRunStatus
              }
              deleteCount={selectedExceptionRecords.length}
              deleteDescription={
                selectedExceptionRecords.length === 0
                  ? "No selected employees have an existing exception"
                  : "Remove existing exception(s)"
              }
            />
          )
        }
      >
        <ReusableTable 
          columns={employeeColumns} 
          data={filteredEmployees} 
          renderCell={renderEmployeeCell} 
          showPagination={true}
          height="auto"
          rowsPerPageOptions={[50, 100]}
          filters={tableFilters}
          onFilterChange={handleTableFilterChange}
        />
      </ResourcesTableContainer>

      {/* Delete Exception Dialog */}
        {deleteDialogOpen && exceptionToDelete && (
          <DeleteExceptionDialog
            deleteDialogOpen={deleteDialogOpen}
            setDeleteDialogOpen={setDeleteDialogOpen}
            exceptionToDelete={exceptionToDelete}
            submitLoading={deleteLoading}
            handleConfirmDelete={handleConfirmDelete}
          />
        )}

        <BulkDeleteExceptionDialog
          open={bulkDeleteOpen}
          onCancel={() => setBulkDeleteOpen(false)}
          onConfirm={handleConfirmBulkDelete}
          loading={deleteLoading}
          count={selectedExceptionRecords.length}
          previewItems={selectedExceptionRecords.map((rec) => ({
            label: rec.employee_email,
            reason: rec.reason,
            validUntil: rec.exception_valid_till,
          }))}
          entityLabel="exception"
        />

        {/* Create Exception Dialog */}
        <Dialog 
          open={createExceptionOpen} 
          onClose={handleCloseCreateException} 
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              minWidth: 600,
              maxWidth: 700,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              fontSize: "1.25rem",
              color: theme.customColors.text.strong,
              background: theme.customColors.background.cardHeader,
              borderBottom: `1px solid ${theme.customColors.border.main}`,
              pb: 2,
              pt: 2,
              px: 3,
            }}
          >
            Create Exception for {selectedEmployees.length} Employee{selectedEmployees.length !== 1 ? 's' : ''}
          </DialogTitle>
          <DialogContent
            sx={{
              px: 3,
              pt: 3,
              pb: 0,
              "&.MuiDialogContent-root": {
                paddingTop: 3,
              },
            }}
          >
            <ExceptionDetailsForm
              reason={exceptionFormData.reason}
              setReason={(val) => setExceptionFormData({ ...exceptionFormData, reason: val })}
              validUntil={exceptionFormData.validUntil}
              setValidUntil={(val) => setExceptionFormData({ ...exceptionFormData, validUntil: val })}
              isValidUntilPast={isValidUntilPast}
            />
          </DialogContent>
          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              px: 3,
              pb: 2,
              pt: 2,
              borderTop: `1px solid ${theme.customColors.border.subtle}`,
              mt: 1,
            }}
          >
            <Button
              onClick={handleCloseCreateException}
              disabled={creatingException}
              variant="outlined"
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.8125rem",
                px: 2,
                py: 0.75,
                borderRadius: 1,
                borderColor: theme.customColors.border.main,
                color: theme.customColors.text.strong,
                "&:hover": {
                  borderColor: theme.customColors.border.strong,
                  backgroundColor: theme.customColors.background.hover,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateException}
              variant="contained"
              disabled={!exceptionFormData.reason?.trim() || !exceptionFormData.validUntil || isValidUntilPast || creatingException}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.8125rem",
                px: 2,
                py: 0.75,
                borderRadius: 1,
                bgcolor: theme.customColors.button.primary,
                color: theme.customColors.button.text,
                "&:hover": {
                  bgcolor: theme.palette._blue.dark,
                },
                "&:disabled": {
                  bgcolor: theme.customColors.background.light,
                  color: theme.customColors.text.disabled,
                },
              }}
            >
              {creatingException ? "Creating..." : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default PeopleResourcesTab;
